/* ---------------------------------------------------------------------------
   PALM 360

   De draaiende sieraden. Twaalf producten hebben een draaivideo op een
   draaischijf, maar die stond tot nu toe achteraan in de galerij achter een
   afspeelknop. Vrijwel niemand klikte erop. Dit element haalt hem naar voren en
   laat het sieraad draaien op het moment dat de bezoeker hem in beeld krijgt.

   Waarom een eigen element en niet de video-media van Impact: die speelt alleen
   af als enable_video_autoplay aanstaat, en dat is een instelling voor de hele
   galerij. Zet je hem aan, dan beginnen ook de sfeervideo's verderop uit zichzelf
   te spelen zodra je erlangs veegt. De draai hoort wel automatisch te lopen, de
   rest niet. Vandaar dit ernaast, met een eigen aanjager.

   Eenmaal draaien en dan stoppen, geen eindeloze lus. Een sieraad dat blijft
   ronddraaien terwijl iemand de maattabel leest is onrust, en op een telefoon
   kost het batterij. De draai is een begroeting, geen behang.

   Het element leunt niet op de carrousel van Impact voor het starten, maar
   respecteert hem wel: play() en pause() zijn er omdat MediaCarousel die
   aanroept op het eerste kindelement van elke slide zodra je van slide wisselt.
   Zonder die twee methodes gooit dat een TypeError en staat de hele galerij stil.

   Fase 2 voegt hier de sleepbare variant aan toe: losse frames in plaats van
   video, zodat je het sieraad met je vinger kunt ronddraaien. Video scrubben
   werkt niet, want een gewone MP4 heeft maar eens per twee seconden een
   keyframe en springt dus tussen een handvol standen.
   --------------------------------------------------------------------------- */

const VISIBILITY_THRESHOLD = 0.5;

/* Hoe lang we hoogstens op een rustig moment wachten voor we de video toch
   ophalen. Deze winkel laadt Klaviyo, Judge.me, CookieYes, Klarna en nog wat
   apps; wachten tot de hoofddraad echt leeg is kan daardoor lang duren en dan
   mist de bezoeker de draai. Anderhalve seconde is de bovengrens. */
const IDLE_TIMEOUT = 1500;

/* Hoe lang de poster erover doet om terug te komen na de draai. Moet gelijk lopen met de
   transition op .palm-spin__poster in palm-spin.css; zie #onEnded voor waarom dat telt. */
const POSTER_FADE = 400;

/* Terughoudend bewegen is een systeemvoorkeur, geen detail. Wie dit aanzet krijgt
   het stilstaande posterbeeld en start de draai zelf maar. */
const prefersReducedMotion = () =>
  window.matchMedia('(prefers-reduced-motion: reduce)').matches;

/* Anderhalve megabyte ongevraagd over een tweedegeneratieverbinding duwen, of
   over een verbinding waarvan de bezoeker zelf heeft gezegd dat hij data wil
   sparen, is niet beleefd. Dan blijft het posterbeeld staan en start de draai
   pas als erop getikt wordt. Een lichtere versie aanbieden is geen optie: die
   is er niet in een kwaliteit die door de beugel kan. */
const connectionIsThin = () => {
  const connection = navigator.connection;

  if (!connection) return false;
  if (connection.saveData) return true;

  return connection.effectiveType === 'slow-2g' || connection.effectiveType === '2g';
};

class PalmSpin extends HTMLElement {
  connectedCallback() {
    this._abortController = new AbortController();

    const { signal } = this._abortController;

    this.video = this.querySelector('.palm-spin__video');
    this.poster = this.querySelector('.palm-spin__poster');

    if (!this.video) return;

    /* Het posterbeeld ligt over de video tot die daadwerkelijk loopt. Zonder dat
       flitst er een zwart vlak op het moment dat de video begint, precies op de
       plek waar het sieraad hoort te staan.

       Op 'playing' en niet op 'loadeddata': niet elke browser laadt uit zichzelf
       een eerste frame, en weigert de browser het afspelen (spaarstand op iOS
       bijvoorbeeld) dan blijft het posterbeeld staan in plaats van plaats te
       maken voor een leeg vlak.

       Zolang de video draait blijft de poster weg, en na afloop komt hij terug -
       zie #onEnded. */
    this.video.addEventListener('playing', () => this.#revealVideo(), { signal });
    this.video.addEventListener('ended', () => this.#onEnded(), { signal });

    /* Nogmaals draaien op verzoek. De hele slide is aanklikbaar en niet alleen de
       badge: op een telefoon is een randje van 40 bij 20 pixels geen doel dat je
       raakt. Impact vangt kliks op de galerij alleen af bij media van het type
       image, dus hier komt niets tussen. */
    this.addEventListener('click', () => this.#onRequestReplay(), { signal });

    if (prefersReducedMotion() || connectionIsThin()) return;

    this._observer = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) {
          if (entry.isIntersecting) this.#autoplayOnce();
        }
      },
      { threshold: VISIBILITY_THRESHOLD }
    );

    this._observer.observe(this);
  }

  disconnectedCallback() {
    this._abortController?.abort();
    this._observer?.disconnect();
    clearTimeout(this._rewind);
  }

  /* play() en pause() zijn het contract met MediaCarousel van Impact. Hij pauzeert
     bij elke sliderwissel het eerste kindelement van elke video-slide, en start het
     opnieuw zodra je op die slide uitkomt. Wij zijn zo'n slide.

     Hier wordt niet gewacht: komt de aanroep van de carrousel of van een tik, dan
     staat de bezoeker er met zijn neus bovenop en is uitstel hinderlijk. Het
     wachten zit alleen in de eerste, ongevraagde keer. */
  play() {
    if (!this.video) return;

    /* Staat er nog een terugspoeling klaar van de vorige draai, dan moet die weg. Tikt de
       bezoeker binnen die 400 ms opnieuw op start, dan zou hij anders midden in de nieuwe
       draai terugspringen naar het begin. */
    clearTimeout(this._rewind);

    /* De bedoeling staat meteen vast. Dat is ook wat hem opzegbaar maakt: tikt de
       bezoeker hierna op pauze, dan zet pause() de stand terug op idle en ziet
       #start() dat de opdracht is ingetrokken. */
    this.setAttribute('data-state', 'spinning');

    /* Eén harde regel, ongeacht wie play() aanroept: de video begint nooit te
       laden voor het posterbeeld binnen is. Die poster is het LCP-element en
       weegt 30 kB; de video anderhalve megabyte.

       Dit is geen theorie. De galerij heeft het autoplay-attribuut aanstaan,
       dus MediaCarousel roept play() aan op de video-slide zodra de carrousel
       ergens uitkomt - en dat is sinds de draai vooraan staat slide 0. Zou dat
       bij het laden al gebeuren, dan omzeilde het de hele afweging hieronder.
       Nu kan dat niet meer. */
    if (this.poster && !this.poster.complete) {
      this.#whenPosterIsPainted(() => this.#start());
      return;
    }

    this.#start();
  }

  #start() {
    if (this.getAttribute('data-state') !== 'spinning') return;

    this.#loadSource();

    this.video.play().catch(() => {
      /* Een browser mag afspelen weigeren, bijvoorbeeld in de spaarstand van iOS.
         Dan blijft het posterbeeld staan, en dat is een prima uitkomst. */
      this.setAttribute('data-state', 'idle');
    });
  }

  pause() {
    this.video?.pause();
    this.setAttribute('data-state', 'idle');
  }

  /* De bron staat in een data-attribuut en gaat er pas in als we hem echt nodig
     hebben. Twee redenen.

     De eerste is kwaliteit. Er is keuze uit een mp4 van 1080 bij 1080 en een
     HLS-stream, en die laatste stond hier eerder omdat hij zelf een resolutie
     kiest die bij het scherm past. Dat pakte verkeerd uit: adaptief betekent ook
     dat hij lager mag uitkomen, de ladder gaat tot 480p, en in vijf seconden is
     er geen tijd om op te schalen. Wat de speler in de eerste seconde kiest is
     wat de bezoeker de hele draai ziet. Gemeten scheelt dat een factor zeven in
     fijn detail. Nu dus altijd de mp4, en daarmee altijd de kwaliteit die is
     goedgekeurd.

     De tweede is de pagina. Die mp4 is anderhalve megabyte en het posterbeeld
     eronder is met 30 kB vrijwel altijd het LCP-element van de productpagina.
     Zolang de src leeg is haalt de browser niets op en heeft de poster het net
     voor zich alleen. */
  #loadSource() {
    if (this._sourceLoaded) return;

    const url = this.dataset.source;

    if (!url) return;

    this._sourceLoaded = true;
    this.video.preload = 'auto';
    this.video.src = url;
  }

  #autoplayOnce() {
    if (this._hasAutoplayed) return;

    this._hasAutoplayed = true;
    this.#whenPosterIsPainted(() => this.play());
  }

  /* De volgorde waar het om draait: eerst de poster, dan pas de video.

     decode() belooft dat de afbeelding gedecodeerd en klaar is om te tekenen; is
     hij al binnen, dan is dat meteen. Het frame erna is hij geschilderd en staat
     de LCP-meting vast. Wachten op window 'load' zou netter klinken maar duurt
     op deze winkel seconden door de apps van derden, en dan mist de bezoeker de
     draai waar het allemaal om begonnen was.

     requestIdleCallback erachteraan zodat de fetch niet midden in het opstarten
     van diezelfde apps valt, met een bovengrens zodat we er nooit in blijven
     hangen. */
  #whenPosterIsPainted(run) {
    const painted = this.poster?.decode
      ? this.poster.decode().catch(() => {})
      : Promise.resolve();

    painted.then(() => {
      requestAnimationFrame(() => {
        if ('requestIdleCallback' in window) {
          window.requestIdleCallback(run, { timeout: IDLE_TIMEOUT });
        } else {
          run();
        }
      });
    });
  }

  #onEnded() {
    /* Terug naar het begin, niet naar het laatste frame. De eerste stand is de
       stand waarop het sieraad gefotografeerd is en waarop de rest van de galerij
       is afgestemd.

       En het posterbeeld komt er weer overheen. Dat was eerder precies andersom:
       de poster die Shopify uit de video perst was 30 kB en duidelijk zachter dan
       het videoframe, dus toen was blijven staan op de video de betere keuze. Die
       posters zijn nu vervangen door het eerste frame van de bronvideo, en daarmee
       is de verhouding omgedraaid - over alle twaalf producten gemeten ligt de
       poster 6 tot 52 procent hoger in fijn detail.

       Dus: de draai zie je in beweging, en wat daarna blijft staan is het scherpste
       beeld dat we hebben. */
    this.setAttribute('data-state', 'idle');
    this.removeAttribute('data-ready');

    /* Terugspoelen gebeurt pas als de poster dekkend is, en dat is niet vrijblijvend.

       Niet elke draai eindigt waar hij begon. Dat is een bewuste keuze per sieraad - de
       Palm Ring maakt bijvoorbeeld een halve slag - en dat hoort ook zo te kunnen. Maar
       currentTime meteen op nul zetten laat het sieraad dan zichtbaar terugspringen op het
       moment dat de video stopt.

       Door eerst de poster in te faden en pas daarna terug te spoelen, gebeurt die sprong
       achter een dekkend beeld. Wat de bezoeker ziet is een overvloeiing van de eindstand
       naar de beginstand, en dat is precies wat het is. Eindigt een draai wel waar hij
       begon, zoals bij de Twin Ring, dan valt er niets te overvloeien en ziet het er net zo
       uit als voorheen. */
    clearTimeout(this._rewind);
    this._rewind = setTimeout(() => {
      this.video.currentTime = 0;
    }, POSTER_FADE);
  }

  #onRequestReplay() {
    if (!this.video) return;

    if (this.video.paused) {
      this.video.currentTime = 0;
      this.play();
    } else {
      this.pause();
    }
  }

  #revealVideo() {
    this.setAttribute('data-ready', '');
  }
}

if (!window.customElements.get('palm-spin')) {
  window.customElements.define('palm-spin', PalmSpin);
}

/* ---------------------------------------------------------------------------
   PALM 360 - de volgorde rechtzetten

   De draaivideo staat in de galerij vooraan maar in de admin op zijn oude plek,
   zodat de collectiekaart, de winkelwagen en de productfeeds hun gewone foto
   houden. Snippets/product-gallery.liquid rekent daarvoor twee dingen om:
   initial-index en filtered-indexes.

   Het derde zit in theme.js en is niet van ons. Kiest de bezoeker een andere
   variant, dan doet ProductGallery dit:

       carousel.select(variant.featured_media.position - 1)

   Dat is de plek in de admin, en die loopt niet meer gelijk met de plek in de
   galerij: alles voor de draaivideo is een slide opgeschoven. Op een product met
   twee afwerkingen kom je zo op de foto van de andere uit.

   Dit element zet de keuze daarna recht, en zoekt de slide op media-id in plaats
   van op een nummer. Dat is meteen bestand tegen elke verdere verschuiving.

   In een microtask, niet direct: de luisteraar van Impact zit op hetzelfde
   formulier en moet eerst klaar zijn, anders overschrijft die ons weer. Een
   microtask loopt na alle luisteraars van deze gebeurtenis en nog voor het
   scherm opnieuw tekent, dus van de tussenstand ziet niemand iets.
   --------------------------------------------------------------------------- */

class PalmSpinGallery extends HTMLElement {
  connectedCallback() {
    this._abortController = new AbortController();

    const form = document.forms[this.getAttribute('form')];

    if (!form) return;

    form.addEventListener('variant:change', (event) => this.#onVariantChanged(event), {
      signal: this._abortController.signal,
    });
  }

  disconnectedCallback() {
    this._abortController?.abort();
  }

  #onVariantChanged(event) {
    /* Hangen er geen foto's aan de varianten, dan laat Impact de galerij met rust
       en valt er ook niets recht te zetten. Dat is het geval bij bijna alle
       producten met een draai; alleen de Gentleman Cuff heeft per afwerking een
       eigen foto. */
    const mediaId = event.detail?.variant?.featured_media?.id;

    if (!mediaId) return;

    const gallery = this.closest('product-gallery');

    if (!gallery) return;

    queueMicrotask(() => {
      for (const carousel of gallery.querySelectorAll('media-carousel')) {
        const slides = carousel.items ?? Array.from(carousel.children);
        const index = slides.findIndex(
          (slide) => slide.getAttribute('data-media-id') === String(mediaId)
        );

        if (index >= 0) carousel.select(index, { animate: false });
      }
    });
  }
}

if (!window.customElements.get('palm-spin-gallery')) {
  window.customElements.define('palm-spin-gallery', PalmSpinGallery);
}
